import {
  ApiClient,
  getTag,
  getTagFromNode,
  GetViewInput,
  sendNotification,
  useApiClient,
  useTranslation,
  useUrlContext,
} from '@msdk/common'
import {
  ControlValidationType,
  Entity,
  EntityMetadata,
  EntityModel,
  FormFieldDefinition,
  FormLookupFieldDefinition,
  FormOpenMode,
  LinkDefinition,
  LookupFieldDefinition,
  LookupMode,
  LookupValue,
  readBaseMetadataByNameAndModule,
  readBaseMetadataForLinkedEntities,
  readEntityModelByName,
  StringValue,
} from '@msdk/definitions'
import {
  Autocomplete,
  ButtonIcon,
  CloseIcon,
  Divider,
  FormField,
  InputAdornment,
  MenuIcon,
  Spinner,
  styled,
  TextField,
} from '@msdk/ui'
import React, { SyntheticEvent, useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'

import {
  useComponentModal,
  useDebounce,
  useFieldLabel,
  useFieldSeverity,
  useFieldValue,
  useHasMandatoryValidation,
} from '../../../hooks'
import { FieldProps } from '../../../models/form'
import { useEntityFromCollection } from '../../../state'
import { filtersToObject, nodeHasFlag } from '../../../utils'
import { ModalSuspenseWrapper } from '../../ModalSuspenseWrapper/ModalSuspenseWrapper'
import { ValidationMessages } from '../../ValidationMessages'
import { LookupModal } from './LookupModal'

const retrieveLookUpValue = async (
  entity: Entity,
  fd: LookupFieldDefinition,
  formDef: FormFieldDefinition,
  api: ApiClient,
): Promise<LookupValue> => {
  const baseMetadata = readBaseMetadataForLinkedEntities(fd.entityName, formDef.formName)
  const link = fd.links.find((l) => l.linkedEntity === entity.entityName) as LinkDefinition
  const lookup = await api.entity.getLookup(baseMetadata?.msProvider as string, entity.entityName, entity.id, fd, link)

  return lookup
}

export function LookupField({ node, onChange, fieldArgs }: FieldProps) {
  const fd = node.value.fieldDefinition as LookupFieldDefinition
  const formDef = node.value.formDefinition as FormLookupFieldDefinition
  const fieldLabel = useFieldLabel(fd, formDef)

  const entity = useEntityFromCollection(node.value.entityId as string)
  const [value, setValue] = useFieldValue(node, onChange)
  const t = useTranslation()
  const api = useApiClient()
  const { createModal } = useComponentModal()
  const { moduleName } = useParams()

  const hasMandatoryFlag = nodeHasFlag(node, ControlValidationType.Mandatory)
  const hasMandatory = useHasMandatoryValidation(node, fd.entityName)

  const isRequired = (hasMandatory || hasMandatoryFlag) && fieldArgs?.formOpenMode !== FormOpenMode.Readonly

  const [autocompleteOpen, setAutocompleteOpen] = useState(false)
  const endAutocomplete = useRef<() => void>()
  const [autocompleteOptions, setAutocompleteOptions] = useState<Entity[]>([])
  const [autocompleteLoading, setAutocompleteLoading] = useState(false)
  const [inputText, setInputText] = useState('')
  const [inputValue, setInputValue] = useState('')
  const debouncedInput = useDebounce(inputText, 300)
  const { context } = useUrlContext()

  useEffect(() => () => endAutocomplete.current?.(), [])

  if (fd.visualization == LookupMode.Dropdown && fd.links.length > 1) {
    const title = t('', 'Lookup Error')
    const desc = t('', 'Drop down visualization with multi entity lookup is not supported')
    sendNotification({ title, message: desc, status: 'error' })
    throw new Error('Drop down visualization with multi entity lookup is not supported')
  }

  const fetchView = async () => {
    const inlineSearch = fd.inlineSearchView ? fd.inlineSearchView : `${fd.entityName}_${fd.name}_INLINE_SEARCH_VIEW`
    const link = fd.links[0]
    const viewName = fd.visualization === LookupMode.Default ? inlineSearch : link.viewName

    const baseMetadata = readBaseMetadataByNameAndModule(link.linkedEntity, link.viewName) as EntityMetadata
    const model = readEntityModelByName(link.linkedEntity) as EntityModel
    const contextEntity = model.parent
    const contextId = context[contextEntity]

    const filters = filtersToObject(fd.viewFilterFields, entity, node.value.listName, node.value.rowId)
    if (debouncedInput) {
      filters['@SEARCH'] = new StringValue(debouncedInput)
    }

    const viewParams: GetViewInput | undefined = {
      pageSize: fd.pageSize === -1 ? undefined : fd.pageSize || 10,
      filters,
      contextName: contextEntity,
      contextId,
      parentId: contextId,
    }

    try {
      const response = await api.entity.getView(baseMetadata.msProvider, model, viewName, viewParams)
      return response
    } catch (error) {
      const title = t('', 'Lookup Search Error')
      const desc = t('', 'Something went wrong while retrieving dropdown data')
      sendNotification({ title, message: desc, status: 'error' })
      return { source: [] }
    }
  }

  useEffect(() => {
    let active = true
    if (debouncedInput && debouncedInput !== '' && !fd.isMultiEntity) {
      setAutocompleteLoading(true)
      const fetch = async () => {
        const response = await fetchView()
        if (active) {
          setAutocompleteOptions(response.source ?? [])
          setAutocompleteLoading(false)
        }
      }
      fetch().catch(console.error)
    } else {
      setAutocompleteOptions([])
    }

    return () => {
      active = false
    }
  }, [debouncedInput])

  const clear = () => {
    node.value.isDirty = true
    setValue(null)
    setInputText('')
    setInputValue('')
  }

  const onInputChange = (_: SyntheticEvent<Element, Event>, valueFromEvent: string, reason: string) => {
    setInputValue(valueFromEvent)
    // Only update the input text state if the change reason is 'input' => it means input from user typing
    if (reason === 'input') {
      setInputText(valueFromEvent)

      if (!valueFromEvent || valueFromEvent === '') {
        node.value.isDirty = true
        setValue(null)
      }
    }
  }

  const onValueChange = async (_: SyntheticEvent<Element, Event>, valueFromEvent: Entity) => {
    const endInteraction = fieldArgs?.beginInteraction?.()
    try {
      if (valueFromEvent) {
        const lookup = await retrieveLookUpValue(valueFromEvent, fd, formDef, api)
        node.value.isDirty = true
        setValue(lookup)
      } else {
        clear()
      }
    } finally {
      endInteraction?.()
    }
  }

  const onModalDismiss = async (entityFromEvent: Entity) => {
    if (entityFromEvent) {
      const lookup = await retrieveLookUpValue(entityFromEvent, fd, formDef, api)
      node.value.isDirty = true
      setValue(lookup)
    }
  }

  const onSearchClick = () => {
    const filters = filtersToObject(fd.viewFilterFields, entity, node.value.listName, node.value.rowId)
    const endInteraction = fieldArgs?.beginInteraction?.()
    const modal = createModal(
      <ModalSuspenseWrapper>
        <LookupModal
          links={fd.links}
          filters={filters}
          modalSize={formDef.modalSize}
          selectedRowId={value?.value?.toString() ?? undefined}
        />
      </ModalSuspenseWrapper>,
      async (selected) => {
        try {
          await onModalDismiss(selected)
        } finally {
          endInteraction?.()
        }
      },
    )
    modal.open()
  }

  const onClear = () => {
    clear()
  }

  const getAutocompleteLabel = (entityFromEvent: Entity | string) => {
    if (typeof entityFromEvent === 'string') {
      return value?.toStringForView() ?? ''
    } else {
      const link = fd.links[0]
      const fieldsForDescription = (fd.inlineSearchFields || fd.linkedFields || link.linkedFields || '')
        .replace(/ /g, '')
        .split(',')

      const stringFields: string[] = []
      fieldsForDescription.forEach((f) => {
        const stringed = entityFromEvent.fields[f]?.toStringForView()
        if (stringed) stringFields.push(stringed)
      })

      return stringFields.join(' - ')
    }
  }

  const onAutocompleteOpen = async () => {
    endAutocomplete.current ??= fieldArgs?.beginInteraction?.()
    setAutocompleteOpen(true)
    if (fd.visualization === LookupMode.Dropdown) {
      setAutocompleteLoading(true)
      const response = await fetchView()
      setAutocompleteOptions(response.source ?? [])
      setAutocompleteLoading(false)
    }
  }

  const onAutocompleteClose = () => {
    setAutocompleteOpen(false)
    setAutocompleteOptions([])
    setInputText('')
    endAutocomplete.current?.()
    endAutocomplete.current = undefined
  }

  const isReadOnly = node.value.state === 'Readonly' || fieldArgs?.formOpenMode === FormOpenMode.Readonly
  const severity = useFieldSeverity(fd, node)

  const StyledButtonIcon = styled(ButtonIcon)`
    margin-right: -7px;
  `

  const StyledDivider = styled(Divider)({
    '&.MuiDivider-root': {
      height: '16px',
      alignSelf: 'center',
      borderRightWidth: '1px',
    },
  })

  return (
    <>
      <FormField
        required={(hasMandatory || hasMandatoryFlag) && fieldArgs?.formOpenMode !== FormOpenMode.Readonly}
        size="small"
        data-test-id={getTagFromNode(fd, moduleName)}
        severity={severity}
        readOnly={isReadOnly}
        disabled={isReadOnly}
      >
        <Autocomplete
          disableClearable={fd.visualization !== LookupMode.Dropdown || !value?.value}
          freeSolo={fd.visualization === LookupMode.Default}
          size="small"
          severity={severity}
          readOnly={isReadOnly}
          options={autocompleteOptions}
          open={autocompleteOpen}
          onOpen={onAutocompleteOpen}
          onClose={onAutocompleteClose}
          disabled={isReadOnly}
          onInputChange={onInputChange}
          onChange={onValueChange}
          inputValue={inputValue}
          value={value ? value.value : ''}
          loading={autocompleteLoading}
          getOptionLabel={getAutocompleteLabel}
          isOptionEqualToValue={(opt: Entity, fieldValue: LookupValue) => opt.id === fieldValue?.value}
          noOptionsText={t('LBL_NO_OPTIONS_LOOKUP', 'No Options')}
          renderInput={(params) =>
            fd.visualization === LookupMode.Dropdown ? (
              <TextField required={isRequired} {...params} label={fieldLabel} />
            ) : (
              <TextField
                required={isRequired}
                {...params}
                label={fieldArgs?.showLabel ? fieldLabel : undefined}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <InputAdornment position="end">
                      {autocompleteLoading && (
                        <ButtonIcon>
                          <Spinner color="primary" size={24} />
                        </ButtonIcon>
                      )}
                      {value && !isReadOnly && (
                        <>
                          <StyledButtonIcon color="primary" aria-label="Clear" onClick={onClear} sx={{ mr: 0 }}>
                            <CloseIcon />
                          </StyledButtonIcon>
                          <StyledDivider orientation="vertical" />
                          <StyledButtonIcon
                            color="primary"
                            aria-label="Upload"
                            onClick={onSearchClick}
                            data-test-id={getTag(moduleName, fd.entityName, 'open_lookup', 'button')}
                          >
                            <MenuIcon />
                          </StyledButtonIcon>
                        </>
                      )}
                      {!value && !isReadOnly && (
                        <StyledButtonIcon
                          color="primary"
                          aria-label="Upload"
                          onClick={onSearchClick}
                          data-test-id={getTag(moduleName, fd.entityName, 'open_lookup', 'button')}
                        >
                          <MenuIcon />
                        </StyledButtonIcon>
                      )}
                    </InputAdornment>
                  ),
                }}
              />
            )
          }
        />
        <ValidationMessages messages={node.value.validationMessages} fieldDef={fd} node={node} />
      </FormField>
    </>
  )
}
